import { AnimatePresence, motion } from "motion/react";
import { useLocation, useParams, useSearch } from "wouter";
import { safeReturnTo } from "@/lib/stepUp.ts";
import { HeaderUtilities } from "../../components/layout/HeaderUtilities.tsx";
import { ConfirmNumberStep } from "./ConfirmNumberStep.tsx";
import { OnboardingHeader } from "./OnboardingHeader.tsx";
import { SubmitPhoneStep } from "./SubmitPhoneStep/SubmitPhoneStep.tsx";
import { VerifyPhoneStep } from "./VerifyPhoneStep.tsx";

/** The two screens, in order, by URL slug. */
const SLUGS = ["phone-number", "verify-phone"] as const;

/**
 * Onboarding is two screens: give us a number, prove it's yours. Everything
 * that used to follow — calendars, addresses, protected meetings, meeting
 * preferences — is asked by a card in the setup conversation instead, so what
 * is left here is only the part that has to happen before there is a
 * conversation to have: making the user reachable.
 *
 * Path-based, with no step state. Each screen knows the one place it goes next,
 * which is all a two-screen flow needs; the step arrays, the progress scan, and
 * the completed/skipped bookkeeping existed to order a list that no longer has
 * anything in it.
 */
export const Onboarding = () => {
  const params = useParams<{ step?: string }>();
  const [, setLocation] = useLocation();
  // Set when a signed-in account was sent here to prove its number before an
  // action that needs it; the flow ends back there instead of at home.
  const returnTo = safeReturnTo(new URLSearchParams(useSearch()).get("returnTo"));
  const withReturn = (path: string) =>
    returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path;

  const index = SLUGS.indexOf((params.step ?? "") as (typeof SLUGS)[number]);
  // An unknown or absent slug starts at the beginning rather than rendering
  // nothing: this route is also where a phone-less user is sent from home.
  const current = index === -1 ? 0 : index;
  const slug = SLUGS[current];

  // Both screens end the same way when no setup thread comes back, which only
  // happens when the server could not find or make one. The number is verified
  // either way, and that is the whole of what onboarding is for, so home is the
  // right landing rather than a screen with nothing to ask; the next visit to
  // onboarding hands over the thread. A step-up ends at whatever it interrupted.
  const done = () => setLocation(returnTo ?? "/", { replace: true });

  const renderStep = () => {
    if (params.step === "confirm-number") return <ConfirmNumberStep returnTo={returnTo} />;
    if (slug === "phone-number") {
      return (
        <SubmitPhoneStep
          onCodeSent={() => setLocation(withReturn("/onboarding/verify-phone"))}
          onDone={done}
        />
      );
    }
    return (
      <VerifyPhoneStep
        onBack={() =>
          setLocation(
            withReturn(returnTo ? "/onboarding/confirm-number" : "/onboarding/phone-number"),
          )
        }
        onDone={done}
        returnTo={returnTo}
      />
    );
  };

  return (
    <div className="h-dvh flex flex-col relative">
      <OnboardingHeader
        currentStep={current}
        totalSteps={SLUGS.length}
        showLogout={returnTo === null && params.step !== "confirm-number"}
      />

      <div className="flex-1 relative overflow-hidden mx-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={slug}
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0 overflow-y-auto"
          >
            <div className="md:max-w-3xl w-full mx-auto">{renderStep()}</div>
          </motion.div>
        </AnimatePresence>
      </div>
      <HeaderUtilities />
    </div>
  );
};
